import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { lenisStore } from "../lib/lenisStore";

const DURATION = 5000;
const EASE = [0.22, 1, 0.36, 1];

export default function Slideshow({ images, onClose }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const current = images[index];

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(next, DURATION);
    return () => clearTimeout(t);
  }, [playing, index, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    lenisStore.instance?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      lenisStore.instance?.start();
      document.body.style.overflow = "";
    };
  }, [onClose, next, prev]);

  return (
    <motion.div
      data-testid="slideshow-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 bg-black"
    >
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={current.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <motion.img
              data-testid="slideshow-image"
              src={current.url}
              alt={current.title}
              initial={{ scale: 1.02, x: 0, y: 0 }}
              animate={
                playing
                  ? { scale: 1.12, x: index % 2 === 0 ? -18 : 18, y: index % 2 === 0 ? 10 : -10 }
                  : { scale: 1.08 }
              }
              transition={{ duration: playing ? DURATION / 1000 + 1.2 : 0.8, ease: "linear" }}
              className="h-full w-full object-cover"
            />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 pointer-events-none" />
      </div>

      <span className="absolute top-6 left-6 md:left-12 z-10 text-[11px] font-mono tracking-[0.25em] text-stone-400">
        {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
      </span>
      <button
        data-testid="slideshow-close"
        onClick={onClose}
        aria-label="Close slideshow"
        className="absolute top-5 right-5 md:right-11 z-10 p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
      >
        <X size={18} />
      </button>

      <div className="absolute bottom-24 md:bottom-28 left-6 md:left-12 right-6 z-10 max-w-lg">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            data-testid="slideshow-caption"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-champagne">
              {current.category} · {current.location}
            </p>
            <h3 className="mt-2 font-serif text-2xl md:text-3xl font-light text-ivory">
              {current.title}
            </h3>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-16 left-6 right-6 md:left-12 md:right-12 z-10 flex gap-1.5">
        {images.map((img, i) => (
          <button
            key={img.id}
            data-testid={`slideshow-segment-${img.id}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to ${img.title}`}
            className="relative h-[3px] flex-1 bg-white/20 overflow-hidden"
          >
            {i < index && <span className="absolute inset-0 bg-champagne/60" />}
            {i === index &&
              (playing ? (
                <motion.span
                  key={`${img.id}-playing`}
                  data-testid="slideshow-progress"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: DURATION / 1000, ease: "linear" }}
                  className="absolute inset-y-0 left-0 bg-champagne"
                />
              ) : (
                <span className="absolute inset-0 bg-champagne/40" />
              ))}
          </button>
        ))}
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
        <button
          data-testid="slideshow-prev"
          onClick={prev}
          aria-label="Previous slide"
          className="p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          data-testid="slideshow-toggle"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Pause slideshow" : "Play slideshow"}
          className="p-3 bg-champagne text-obsidian hover:bg-ivory transition-colors duration-300"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button
          data-testid="slideshow-next"
          onClick={next}
          aria-label="Next slide"
          className="p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}
